import { useEffect, useRef } from 'react';
import ws from 'lib/ws';

const TEAM_CHANNEL_EVENT = 'TEAM_CHANNEL_UPDATE';

// Module-level room refcount so several components subscribing to the SAME team
// (e.g. the list + an open results view) share one server-side room membership.
// We only emit leaveRoom when the LAST subscriber unmounts — otherwise one unmount
// would pull the room out from under the others and silently kill their updates.
const roomCounts = new Map();
let connectRejoinBound = false;

// Bind a single 'connect' handler (once) that re-joins every room we still hold a
// subscriber for. socket.io auto-reconnects drop server-side room membership, so we
// must re-emit joinRoom after each (re)connect.
const ensureConnectRejoin = () => {
  if (connectRejoinBound) return;
  connectRejoinBound = true;
  ws.on('connect', () => {
    roomCounts.forEach((count, roomId) => {
      if (count > 0) ws.send('joinRoom', { roomId });
    });
  });
};

const acquireRoom = (roomId) => {
  ensureConnectRejoin();
  const next = (roomCounts.get(roomId) || 0) + 1;
  roomCounts.set(roomId, next);
  if (next === 1) ws.send('joinRoom', { roomId }); // first subscriber → join
};

const releaseRoom = (roomId) => {
  const next = (roomCounts.get(roomId) || 1) - 1;
  if (next <= 0) {
    roomCounts.delete(roomId);
    ws.send('leaveRoom', { roomId }); // last subscriber → leave
  } else {
    roomCounts.set(roomId, next);
  }
};

/**
 * Subscribe to realtime Team Channel updates for a given team/channel.
 *
 * Joins the `team_channel_<teamId>` room over the shared app socket (the single
 * connection opened by ws.connect() at app mount) — matching the backend handler,
 * which listens on the `message` event and reads `data.roomId`. Listens for
 * TEAM_CHANNEL_UPDATE and invokes `onEvent(evt)` for each. Room membership is
 * refcounted, so multiple concurrent subscribers to the same team are safe.
 *
 * `onEvent` is held in a ref so a new handler identity per render does NOT
 * re-subscribe (which would churn join/leave on every parent re-render).
 *
 * Event payload:
 *   { entity:'poll'|'quiz'|'potm',
 *     action:'created'|'updated'|'deleted'|'state_changed'|'results_updated',
 *     data?, id, teamId, at }
 */
const useTeamChannelUpdates = (teamId, onEvent) => {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!teamId) return undefined;
    const roomId = `team_channel_${teamId}`;
    const listener = (evt) => handlerRef.current && handlerRef.current(evt);

    acquireRoom(roomId);
    ws.on(TEAM_CHANNEL_EVENT, listener);

    return () => {
      ws.off(TEAM_CHANNEL_EVENT, listener);
      releaseRoom(roomId);
    };
  }, [teamId]);
};

export default useTeamChannelUpdates;
